import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Download, Plus, Search, Upload } from "lucide-react";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import InputCst from "@components/InputCst/InputCst";
import ListItemCardCst from "@components/ListItemCardCst";
import PaginatorCst from "@components/PaginatorCst";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL, downloadFile, uploadFile } from "@utils/utilsApi";
import { MethodType } from "src/services/type";
import UploadModalCst from "@components/UploadModalCst";
import { PagesID } from "@utils/utilsConfigurations";
import { mapEntityToCard } from "@pages/Contents/V2/Corporate/Catalog/mapEntityToCard";
import { mapAggregatedType } from "@pages/Contents/V2/Corporate/Catalog/types";
import AddUsersToGroupModal from "../components/AddUsersToGroupModal";
import { useScrollToHash } from "@hooks/useScrollToHash";
import { LO_SECTION_ANCHOR, SECTION_ANCHOR_SCROLL_MARGIN } from "../types";

const PAGE_SIZE = 10;

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const theme = useTheme();
  return (
    <Typography
      sx={{
        fontSize: "20px",
        fontWeight: 700,
        color: theme.palette.primary.main,
      }}
    >
      {children}
    </Typography>
  );
};

const StaticGroupDetail: React.FC = () => {
  const { t } = useTranslation("group-detail-v2");
  const { t: tCatalog } = useTranslation("catalog-v2");
  const { t: tWizard } = useTranslation("group-wizard-v2");
  const theme = useTheme();
  const { id: corporateId = "", groupId = "" } = useParams();

  const { dataDetail: groupDetail } = useFetchDetail(
    `${ServicesURL.staticGroups}/static`,
    groupId,
    PagesID["corporate.groups"],
  );

  useScrollToHash(!!groupDetail);

  const initiativesUrl = `${ServicesURL.getEnrollmentMassive}?corporateId=${corporateId}&groupId=${groupId}`;
  const { items: initiatives, isLoading: loadingInit } = usePaginatedList(
    initiativesUrl,
    { page: 1, pageSize: PAGE_SIZE },
  );

  const [addUsersOpen, setAddUsersOpen] = useState(false);

  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize, setUsersPageSize] = useState(PAGE_SIZE);
  const [usersSearch, setUsersSearch] = useState("");
  const [csvOpen, setCsvOpen] = useState(false);
  const usersUrl = `${ServicesURL.staticGroups}/v3/${corporateId}/static/${groupId}/users`;
  const {
    items: users,
    totalItems: usersTotal,
    totalPages: usersPages,
    isLoading: loadingUsers,
    mutate: mutateUsers,
  } = usePaginatedList(
    usersUrl,
    {
      page: usersPage,
      pageSize: usersPageSize,
      search: usersSearch || undefined,
    },
    { responseKey: "response" },
  );

  const [loPage, setLoPage] = useState(1);
  const [loPageSize, setLoPageSize] = useState(PAGE_SIZE);
  const [loSearch, setLoSearch] = useState("");
  const loUrl = `${ServicesURL.getLOEnrolledWithEnrollInfo
    .replace(":corporateId", corporateId)
    .replace(":groupId", groupId)}?isStandalone=true`;
  const {
    items: los,
    totalItems: loTotal,
    totalPages: loPages,
    isLoading: loadingLo,
  } = usePaginatedList(loUrl, {
    page: loPage,
    pageSize: loPageSize,
    search: loSearch || undefined,
  });

  const searchBoxSx = {
    flex: 1,
    minWidth: 220,
    "& .MuiFormControl-root": {
      minWidth: "0 !important",
      width: "100% !important",
    },
    "& .MuiOutlinedInput-root": { borderRadius: "8px" },
    "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
  };

  return (
    <AnimationFadeIn>
      <Box
        sx={{
          py: 3,
          pr: 3,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <Box>
          <Breadcrumb />
          <Typography sx={{ fontSize: "28px", fontWeight: 700, mt: 1 }}>
            {groupDetail?.name || "-"}
          </Typography>
          <Typography
            sx={{ fontSize: "13px", color: theme.palette.text.primary }}
          >
            ID: {groupId}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <SectionTitle>{t("initiatives.title")}</SectionTitle>
            {/* TODO (BE Mancante): associazione iniziativa al gruppo
                ripristinare il pulsante quando l'endpoint sarà disponibile */}
            {/* <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 700,
              }}
            >
              {t("initiatives.associate")}
            </Button> */}
          </Box>
          {loadingInit ? (
            <CircularProgress />
          ) : initiatives.length === 0 ? (
            <Typography sx={{ color: theme.palette.text.primary }}>
              {t("initiatives.empty")}
            </Typography>
          ) : (
            initiatives.map((row: any, index: number) => (
              <ListItemCardCst
                key={row.id ?? index}
                title={row.name || row.initiativeName || t("initiatives.item")}
                subtitle={{ id: row.id ?? row.enrollmentId }}
                badges={[]}
                actions={[]}
              />
            ))
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <SectionTitle>{t("users.title")}</SectionTitle>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<Plus size={16} />}
                onClick={() => setAddUsersOpen(true)}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 700,
                }}
              >
                {t("users.addUser")}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download size={16} />}
                onClick={() =>
                  downloadFile(
                    `${ServicesURL.staticGroups}/static/${groupId}/users/external-id`,
                  )
                }
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  borderColor: theme.palette.text.primary,
                  color: theme.palette.text.primary,
                  "&:hover": { borderColor: theme.palette.text.primary },
                }}
              >
                {t("downloadCsv")}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Upload size={16} />}
                onClick={() => setCsvOpen(true)}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  borderColor: theme.palette.text.primary,
                  color: theme.palette.text.primary,
                  "&:hover": { borderColor: theme.palette.text.primary },
                }}
              >
                {t("bulk.uploadUsers", { ns: "users-groups-v2" })}
              </Button>
            </Box>
          </Box>

          <Box sx={searchBoxSx}>
            <InputCst
              id="group-detail-users-search"
              label={t("search")}
              value={usersSearch}
              onChange={(e: any) => {
                setUsersSearch(e.target.value);
                setUsersPage(1);
              }}
              endAdornment={
                <Search size={16} color={theme.palette.text.tertiary} />
              }
            />
          </Box>

          {loadingUsers ? (
            <CircularProgress />
          ) : users.length === 0 ? (
            <Typography sx={{ color: theme.palette.text.primary }}>
              {t("users.empty")}
            </Typography>
          ) : (
            users.map((u: any) => {
              const memberId = u.user_id ?? u.id_user ?? u.id;
              const externalId = u.external_id ?? u.externalId;
              const fullName = [u.name, u.surname]
                .filter(Boolean)
                .join(" ")
                .trim();
              return (
                <ListItemCardCst
                  key={memberId}
                  title={fullName || externalId || String(memberId)}
                  subtitle={{ id: memberId, activityType: externalId }}
                  badges={[]}
                  actions={[]}
                />
              );
            })
          )}

          {usersPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <PaginatorCst
                page={usersPage}
                pageSize={usersPageSize}
                totalItems={usersTotal}
                onPageChange={setUsersPage}
                onPageSizeChange={(next) => {
                  setUsersPageSize(next);
                  setUsersPage(1);
                }}
              />
            </Box>
          )}
        </Box>

        <Box
          id={LO_SECTION_ANCHOR}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            scrollMarginTop: SECTION_ANCHOR_SCROLL_MARGIN,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <SectionTitle>{t("learningObjects.title")}</SectionTitle>
            {/* TODO (BE Mancante): export CSV oggetti formativi del gruppo */}
            <Button
              variant="outlined"
              startIcon={<Download size={16} />}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 600,
                borderColor: theme.palette.text.primary,
                color: theme.palette.text.primary,
                "&:hover": { borderColor: theme.palette.text.primary },
              }}
            >
              {t("downloadCsv")}
            </Button>
          </Box>

          <Box sx={searchBoxSx}>
            <InputCst
              id="group-detail-lo-search"
              label={t("search")}
              value={loSearch}
              onChange={(e: any) => {
                setLoSearch(e.target.value);
                setLoPage(1);
              }}
              endAdornment={
                <Search size={16} color={theme.palette.text.tertiary} />
              }
            />
          </Box>

          {loadingLo ? (
            <CircularProgress />
          ) : los.length === 0 ? (
            <Typography sx={{ color: theme.palette.text.primary }}>
              {t("learningObjects.empty")}
            </Typography>
          ) : (
            los.map((raw: any, index: number) => {
              const source = raw.lo || raw;
              const entity = {
                ...source,
                title: source.title || source.name || source.loTitle,
                type: source.type || source.loType,
                learning_object_type:
                  source.learning_object_type || source.loType,
              };
              const entityType = mapAggregatedType(entity.type);
              const cardProps = mapEntityToCard(entity, entityType, {
                t: tCatalog,
                onDetails: () => {},
              });
              return (
                <ListItemCardCst
                  key={entity.id ?? index}
                  {...cardProps}
                  actions={[]}
                />
              );
            })
          )}

          {loPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <PaginatorCst
                page={loPage}
                pageSize={loPageSize}
                totalItems={loTotal}
                onPageChange={setLoPage}
                onPageSizeChange={(next) => {
                  setLoPageSize(next);
                  setLoPage(1);
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      <AddUsersToGroupModal
        open={addUsersOpen}
        onClose={() => setAddUsersOpen(false)}
        corporateId={corporateId}
        group={{ id: groupId, name: groupDetail?.name }}
        onSuccess={mutateUsers}
      />

      <UploadModalCst
        open={csvOpen}
        onClose={() => setCsvOpen(false)}
        title={String(tWizard("uploadCsv"))}
        accept=".csv,text/csv"
        banner={{
          title: String(tWizard("csv-modal.banner-title")),
          text: String(tWizard("csv-modal.banner-text")),
          downloadLabel: String(tWizard("csv-modal.banner-download")),
          onDownload: () =>
            downloadFile(
              `${ServicesURL.staticGroups}/static/group/download/template`,
            ),
          secondaryDownloadLabel: String(
            tWizard("csv-modal.banner-download-members"),
          ),
          onSecondaryDownload: () =>
            downloadFile(
              `${ServicesURL.staticGroups}/static/${groupId}/users/external-id`,
            ),
        }}
        onUpload={(file) => {
          const formData = new FormData();
          formData.append("file", file);
          return uploadFile(
            `${ServicesURL.staticGroups}/static/${groupId}`,
            formData,
            MethodType.PUT,
          );
        }}
        onUploaded={() => mutateUsers && mutateUsers()}
      />
    </AnimationFadeIn>
  );
};

export default StaticGroupDetail;
